/**
 * Focos dentro das áreas do filtro dos "Últimos focos" (união de até 3
 * áreas). Funções puras, sem OpenLayers (testadas em areaFires.test.ts).
 *
 * Mesma estratégia do cartão do município (`countInMunicipio.ts`):
 * `indexAreaFires` percorre o conjunto UMA vez (quando o conjunto de focos ou
 * as áreas mudam); a lista usa os últimos índices e a contagem de qualquer
 * janela de tempo é O(log m) por busca binária + soma prefixada — o contador
 * acompanha o loop sem recalcular nada.
 *
 * Associação: municípios e estados comparam o código IBGE quando o foco o tem
 * (diário, ou 10 min com par no diário); os demais focos e as outras áreas
 * usam caixa envolvente + ponto-no-polígono (`areaShape.ts`).
 */
import type { AreaShape } from './areaShape';
import { fireCdMun } from './countInMunicipio';
import { CLASS_GOES, type FireDataset, type WindowCount } from './dataset';
import { indexRange } from './timeFilter';

/** Como decidir se um foco está numa área. */
export interface AreaMatcher {
  /** Pelo código IBGE do município do foco (quando conhecido); null = só geometria. */
  byCode: ((cdMun: number) => boolean) | null;
  /** Partes da área (feições com a mesma chave). */
  shapes: readonly AreaShape[];
}

export interface AreaFires {
  /** Conjunto de origem. */
  ds: FireDataset;
  /** Índices no conjunto dos focos nas áreas (crescente = ordem de tempo). */
  idx: Uint32Array;
  /** Instantes desses focos (crescente). */
  t: Float64Array;
  /** goesPrefix[k] = nº de focos GOES-19 em [0, k). */
  goesPrefix: Uint32Array;
}

function inShapes(shapes: readonly AreaShape[], x: number, y: number): boolean {
  for (const s of shapes) {
    const [minX, minY, maxX, maxY] = s.extent;
    if (x < minX || x > maxX || y < minY || y > maxY) continue;
    if (s.contains(x, y)) return true;
  }
  return false;
}

/** Focos do conjunto em QUALQUER uma das áreas. */
export function indexAreaFires(ds: FireDataset, matchers: readonly AreaMatcher[]): AreaFires {
  const picked: number[] = [];
  const needCode = matchers.some((m) => m.byCode);
  for (let i = 0; i < ds.n; i++) {
    const cd = needCode ? fireCdMun(ds, i) : 0;
    const x = ds.x[i]!;
    const y = ds.y[i]!;
    for (const m of matchers) {
      if (m.byCode && cd !== 0 ? m.byCode(cd) : inShapes(m.shapes, x, y)) {
        picked.push(i);
        break;
      }
    }
  }
  const n = picked.length;
  const idx = Uint32Array.from(picked);
  const t = new Float64Array(n);
  const goesPrefix = new Uint32Array(n + 1);
  for (let k = 0; k < n; k++) {
    const i = idx[k]!;
    t[k] = ds.t[i]!;
    goesPrefix[k + 1] = goesPrefix[k]! + (ds.cls[i] === CLASS_GOES ? 1 : 0);
  }
  return { ds, idx, t, goesPrefix };
}

/** Focos nas áreas com from < t ≤ to, por classe (O(log m)). */
export function countAreaWindow(af: AreaFires, from: number, to: number): WindowCount {
  const [lo, hi] = indexRange(af.t, from, to);
  const goes = af.goesPrefix[hi]! - af.goesPrefix[lo]!;
  return { total: hi - lo, goes, others: hi - lo - goes };
}

/** Índices (no conjunto) dos `limit` focos mais recentes nas áreas, do mais novo para o mais antigo. */
export function recentAreaIndices(af: AreaFires, limit: number): number[] {
  const out: number[] = [];
  for (let k = af.idx.length - 1; k >= 0 && out.length < limit; k--) out.push(af.idx[k]!);
  return out;
}
